import type { ProfileRef } from './types'

// Structural shape of one create/promote error union member as selected by the
// mutations: every member has `__typename` + `message`; only the per-channel
// members (`CreateContentItemVariant*Error`, `Post*Error`) also carry a
// `channelId`.
export type ContentItemError = {
  __typename: string
  message: string
  channelId?: string | null
}

// Semantic error kind → drives the display surface (see surfaceErrors). `content`
// is an inline per-channel error; `limit` is an upgrade prompt. New per-channel
// error types slot in by adding a `__typename` → kind mapping here.
export type SaveErrorKind = 'content' | 'limit'

export type ChannelSaveError = {
  channelId: string
  serviceName: string
  kind: SaveErrorKind
  message: string
}

// Per-channel limit errors, across the create (`createContentItem`) and promote
// (`promoteContentItemDraftToPosts`) payloads, that route to the upgrade prompt.
const LIMIT_ERROR_TYPENAMES = new Set([
  'CreateContentItemVariantLimitReachedError',
  'PostLimitReachedError',
])

const errorKind = (typename: string): SaveErrorKind =>
  LIMIT_ERROR_TYPENAMES.has(typename) ? 'limit' : 'content'

export type ResolvedContentItemErrors = {
  channelErrors: ChannelSaveError[]
  generalErrors: string[]
}

/**
 * Given the channels involved in a save and the errors it came back with,
 * returns the service names whose stale inline error must be cleared: channels
 * that were part of the save but are no longer failing. Fixes a fixed channel
 * keeping its error when a sibling channel still fails (an all-or-nothing
 * failure returns only the still-failing variants). Channels still failing keep
 * their notice, which is replaced in place to avoid flicker.
 */
export function channelsToClear(
  channelErrors: readonly ChannelSaveError[],
  savedServiceNames: readonly string[],
): string[] {
  const failing = new Set(channelErrors.map((error) => error.serviceName))
  return savedServiceNames.filter((serviceName) => !failing.has(serviceName))
}

/**
 * Splits a `CreateContentItemFailure`'s or `PromoteContentItemDraftToPostsFailure`'s
 * errors into per-channel errors (those whose `channelId` maps to a selected
 * profile) and request-level "general" errors. A `channelId` that matches no
 * selected profile falls back to general so its message is never silently
 * dropped.
 */
export function resolveContentItemErrors(
  errors: readonly ContentItemError[],
  profiles: readonly ProfileRef[],
): ResolvedContentItemErrors {
  const channelErrors: ChannelSaveError[] = []
  const generalErrors: string[] = []

  for (const error of errors) {
    const serviceName =
      error.channelId != null
        ? profiles.find((profile) => profile.id === error.channelId)
            ?.serviceName
        : undefined

    if (error.channelId != null && serviceName != null) {
      channelErrors.push({
        channelId: error.channelId,
        serviceName,
        kind: errorKind(error.__typename),
        message: error.message,
      })
    } else {
      generalErrors.push(error.message)
    }
  }

  return { channelErrors, generalErrors }
}
